import { Moment } from 'moment'
import { ReactNode } from 'react'

/**
 * Тип ограничения выбора дат
 */
export enum DateRestriction {
	/** Только прошлые даты (включая сегодня) */
	PAST = 'PAST',
	/** Только будущие даты (включая сегодня) */
	FUTURE = 'FUTURE',
}

/**
 * Вариант отображения инпута даты
 */
export type DateInputVariant = 'primary' | 'inputVariant'

/**
 * Позиция кнопки "Сбросить"
 */
export type ResetButtonPosition = 'left' | 'right'

/**
 * Режим выбора в календаре
 */
export type SelectionMode = 'single' | 'range'

/**
 * Режим отображения календаря
 */
export type CalendarMode = 'days' | 'months' | 'years'

/**
 * Тип пресета для быстрого выбора периода
 */
export type PresetType = 'today' | 'yesterday' | 'week' | 'month' | 'year'

/**
 * Интерфейс периода дат (начало и конец)
 */
export interface DateRange {
	startDate: number
	endDate: number
}

/**
 * Props для DatePicker компонента
 */
export interface DatePickerProps {
	/** Значение выбранной даты */
	value?: Date | string | number
	/** Callback при изменении даты */
	onChange?: (date: Moment | null) => void
	/** Тип ограничения дат */
	restrictionType?: DateRestriction
	/** Диапазон заблокированных дат */
	blockedDatesRange?: [Moment, Moment]
	/** Вариант отображения */
	variant?: DateInputVariant
	/** Количество дней для ограничения выбора */
	daysRangeDisabled?: number
	/** Показать фильтр "Сегодня" */
	isTodayFilter?: boolean
	/** Показать фильтр "Вчера" */
	isYesterdayFilter?: boolean
	/** Показать кнопку "Сбросить" */
	showResetButton?: boolean
	/** Позиция кнопки "Сбросить" */
	resetButtonPosition?: ResetButtonPosition
	/** Placeholder текст */
	placeholder?: string
}

/**
 * Props для DateRangePicker компонента
 */
export interface DateRangePickerProps {
	/** Начальная дата периода */
	startDate?: Date | string | number
	/** Конечная дата периода */
	endDate?: Date | string | number
	/** Callback при изменении периода */
	onChangeUpdateDate?: (dates: [number, number]) => void
	/** Отключить календарь */
	disabled?: boolean
	/** Тип ограничения дат */
	restrictionType?: DateRestriction
	/** Диапазон доступных дат для выбора */
	disabledDates?: { firstItem: number; lastItem: number }
	/** Текущий выбранный диапазон (для автоматического выбора пресета) */
	currentRange?: string
	/** Callback при выборе быстрого периода */
	onPresetSelect?: () => void
	/** Ключ для внешнего сброса активного быстрого периода */
	presetResetKey?: string | number | null
	/** Показать фильтр "Сегодня" */
	todayFilter?: boolean
	/** Показать фильтр "Вчера" */
	yesterdayFilter?: boolean
	/** Показать фильтр "Неделя" */
	weekFilter?: boolean
	/** Показать фильтр "Месяц" */
	monthFilter?: boolean
	/** Показать фильтр "Год" */
	yearFilter?: boolean
	/** Показать кнопку "Сбросить" */
	isShowResetBtn?: boolean
	/** Позиция кнопки "Сбросить" */
	resetButtonPosition?: ResetButtonPosition
	/** Использовать будущие даты для пресетов */
	isNeedFutureDates?: boolean
	/** Сдвиг дней для пресетов */
	dayBefore?: number
	/** Дополнительный контент между пресетами и полем дат */
	middleContent?: ReactNode
}

/**
 * Props для Calendar компонента
 */
export interface CalendarProps {
	/** Текущая отображаемая дата (для навигации) */
	currentDate: Moment
	/** Выбранная дата (для single mode) */
	selectedDate?: Moment | null
	/** Выбранный диапазон дат (для range mode) */
	selectedRange?: [Moment | null, Moment | null]
	/** Режим выбора */
	mode: SelectionMode

	onSelectDate?: (date: Moment) => void

	onSelectRange?: (range: [Moment, Moment]) => void

	isDateDisabled?: (date: Moment) => boolean

	onMonthChange?: (date: Moment) => void
}

export interface CalendarHeaderProps {
	currentDate: Moment

	mode: CalendarMode

	onModeChange: (mode: CalendarMode) => void

	onPrevious: () => void

	onNext: () => void
}

export interface DayCellProps {
	date: Moment

	isSelected: boolean

	isToday: boolean

	isCurrentMonth: boolean

	isDisabled: boolean

	isInRange?: boolean

	isRangeStart?: boolean

	isRangeEnd?: boolean

	onClick: (date: Moment) => void

	onMouseEnter?: () => void

	onMouseLeave?: () => void
}

export interface MonthPickerProps {
	currentYear: number

	selectedMonth?: number

	onSelectMonth: (month: number) => void

	onBack: () => void
}

export interface YearPickerProps {
	selectedYear: number

	onSelectYear: (year: number) => void

	onBack: () => void
}

export interface DateInputProps {
	value?: string

	placeholder?: string

	isOpen: boolean

	isActive?: boolean

	variant?: DateInputVariant

	disabled?: boolean

	onClick: () => void
}

export interface PresetsProps {
	activePreset?: PresetType | null

	availablePresets: PresetType[]

	onSelectPreset: (preset: PresetType) => void

	disabled?: boolean
}
